import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t, plural } from '@lingui/core/macro';
import { client } from '../services/apiClient';
import type {
  BookmarkAction,
  BookmarkSectionActionTemplate,
  BookmarkSectionBatchTarget,
} from '../types/api';
import { useToast } from './useToast';
import { useOptionalBookmarkExitAnimation } from '../contexts/BookmarkExitAnimationContext';

const SECTION_ASYNC_TOAST_MS = 10000;

export function useBatchActions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const exitAnimation = useOptionalBookmarkExitAnimation();

  const batchArchive = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'archive',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: (bookmarkIds) => {
      exitAnimation?.markAsExiting(bookmarkIds);
    },
    onSuccess: (bookmarkIds) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Archived # article',
          other: 'Archived # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchDelete = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'delete',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: (bookmarkIds) => {
      exitAnimation?.markAsExiting(bookmarkIds);
    },
    onSuccess: (bookmarkIds) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Deleted # article',
          other: 'Deleted # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchMove = useMutation({
    mutationFn: async ({
      bookmarkIds,
      folderId,
    }: {
      bookmarkIds: string[];
      folderId: string;
    }) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'move',
        folder_id: folderId,
      }));
      await client.bookmarkActions(actions);
      return { bookmarkIds, folderId };
    },
    onMutate: ({ bookmarkIds }) => {
      exitAnimation?.markAsExiting(bookmarkIds);
    },
    onSuccess: ({ bookmarkIds }) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Moved # article',
          other: 'Moved # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchTag = useMutation({
    mutationFn: async ({
      bookmarkIds,
      addTags = [],
      removeTags = [],
    }: {
      bookmarkIds: string[];
      addTags?: string[];
      removeTags?: string[];
    }) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'tags',
        add_tags: addTags.map((tag) => ({ name: tag })),
        remove_tags: removeTags.map((tag) => ({ name: tag })),
      }));
      await client.bookmarkActions(actions);
      return { bookmarkIds, addTags, removeTags };
    },
    onSuccess: ({ bookmarkIds }) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] }); // refresh sidebar tag counts
      showToast(
        plural(bookmarkIds.length, {
          one: 'Updated tags for # article',
          other: 'Updated tags for # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchLike = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'like',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onSuccess: (bookmarkIds) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Liked # article',
          other: 'Liked # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchUnarchive = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'home',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: (bookmarkIds) => {
      exitAnimation?.markAsExiting(bookmarkIds);
    },
    onSuccess: (bookmarkIds) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Unarchived # article',
          other: 'Unarchived # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchMoveHome = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'home',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: (bookmarkIds) => {
      exitAnimation?.markAsExiting(bookmarkIds);
    },
    onSuccess: (bookmarkIds) => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showToast(
        plural(bookmarkIds.length, {
          one: 'Moved # article',
          other: 'Moved # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const afterSectionMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  };

  const batchSectionArchive = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'archive' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Archiving articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Archived articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to archive articles`, 'error');
    },
  });

  const batchSectionDelete = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'delete' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Deleting articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Deleted articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to delete articles`, 'error');
    },
  });

  const batchSectionMove = useMutation({
    mutationFn: async ({
      section,
      folderId,
    }: {
      section: BookmarkSectionBatchTarget;
      folderId: string;
    }) => {
      const action: BookmarkSectionActionTemplate = {
        action_type: 'move',
        folder_id: folderId,
      };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Moving articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Moved articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to move articles`, 'error');
    },
  });

  const batchSectionTag = useMutation({
    mutationFn: async ({
      section,
      addTags = [],
      removeTags = [],
    }: {
      section: BookmarkSectionBatchTarget;
      addTags?: string[];
      removeTags?: string[];
    }) => {
      const action: BookmarkSectionActionTemplate = {
        action_type: 'tags',
        add_tags: addTags.map((tag) => ({ name: tag })),
        remove_tags: removeTags.map((tag) => ({ name: tag })),
      };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Updating tags… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Updated tags for articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to update tags`, 'error');
    },
  });

  const batchSectionUnarchive = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'home' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Restoring articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Unarchived articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to unarchive articles`, 'error');
    },
  });

  return {
    batchArchive,
    batchDelete,
    batchMove,
    batchMoveHome,
    batchTag,
    batchLike,
    batchUnarchive,
    batchSectionArchive,
    batchSectionDelete,
    batchSectionMove,
    batchSectionTag,
    batchSectionUnarchive,
  };
}
